import { FaceLoginAttemptReason } from '@reliance/types/enums/FaceLoginAttemptReason';
import { FaceLoginAttempt } from '@reliance/types/models/FaceLoginAttempt';
import {
  createColumnHelper,
  type OnChangeFn,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  Row,
} from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import RowLayout from 'react-bootstrap/Row';
import { Link } from 'react-router-dom';
import { formatDate } from '../../../format/DateTime';
import { formatDecimal } from '../../../format/Decimal';
import BaseHtmlTable from '../../../table/BaseHtmlTable';
import {
  attemptTimestamp,
  brightnessWarning,
  FACE_LOGIN_ATTEMPT_REASON_FILTER_OPTIONS,
  FACE_LOGIN_MODE_LABELS,
  FACE_LOGIN_PASSED_FILTER_OPTIONS,
  faceAreaWarning,
  facesWarning,
  poseWarning,
  reasonLabel,
  scoreWarning,
  sessionStyleIndexById,
  sharpnessWarning,
} from './formatting';

export const FACE_LOGIN_ATTEMPT_SERVER_COLUMNS = [
  { id: 'expand' },
  { id: 'time', meta: { sortName: 'dateReceived' } },
  { id: 'reason', meta: { sortName: 'reason' } },
  { id: 'passed', meta: { sortName: 'passed' } },
  { id: 'attempt', meta: { sortName: 'attempt' } },
  { id: 'score', meta: { sortName: 'bestScore' } },
  { id: 'sharpness', meta: { sortName: 'bestSharpness' } },
  { id: 'brightness', meta: { sortName: 'bestBrightness' } },
  { id: 'faces', meta: { sortName: 'maxFaceCount' } },
  { id: 'duration', meta: { sortName: 'durationSeconds' } },
];

type FaceLoginAttemptsTableBaseProps = {
  attempts: FaceLoginAttempt[];
  loading: boolean;
};

type FaceLoginAttemptsTableServerProps = FaceLoginAttemptsTableBaseProps & {
  paginationMode: 'server';
  page?: number;
  totalPages?: number;
  total?: number;
  perPage?: number;
  serverPagination?: PaginationState;
  onServerPaginationChange?: OnChangeFn<PaginationState>;
  serverSorting?: SortingState;
  onServerSortingChange?: OnChangeFn<SortingState>;
  serverColumnFilters?: ColumnFiltersState;
  onServerColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
};

type FaceLoginAttemptsTableProps = FaceLoginAttemptsTableServerProps;

const MetricValue: React.FC<{ value: React.ReactNode; warning?: string | null }> = ({
  value,
  warning,
}) => (
  <div>
    <div>{value}</div>
    {warning ? <div className="text-warning small">{warning}</div> : null}
  </div>
);

const DetailItem: React.FC<{ label: string; value: React.ReactNode; warning?: string | null }> = ({
  label,
  value,
  warning,
}) => (
  <Col md={3} className="mb-2">
    <div className="text-muted small">{label}</div>
    <div>{value}</div>
    {warning ? <div className="text-warning small">{warning}</div> : null}
  </Col>
);

const FaceLoginAttemptsTable: React.FC<FaceLoginAttemptsTableProps> = (props) => {
  const { attempts, loading } = props;
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const sessionIndexes = useMemo(() => sessionStyleIndexById(attempts), [attempts]);

  const columnHelper = createColumnHelper<FaceLoginAttempt>();
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'expand',
        header: '',
        cell: (info) => {
          const isExpanded = expandedRowId === info.row.original.id;
          return (
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none shadow-none"
              onClick={() => setExpandedRowId(isExpanded ? null : info.row.original.id)}>
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
            </Button>
          );
        },
        size: 30,
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor((row) => attemptTimestamp(row), {
        id: 'time',
        header: 'Time',
        cell: (info) => formatDate(info.getValue(), true),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.reason, {
        id: 'reason',
        header: 'Reason',
        cell: (info) => {
          const reason = info.getValue();
          const passed = info.row.original.passed || reason === FaceLoginAttemptReason.SUCCESS;
          return (
            <Badge bg={passed ? 'success' : 'danger'}>
              {reasonLabel(reason)}
            </Badge>
          );
        },
        meta: {
          filterComponent: 'SelectFilter',
          filterOptions: FACE_LOGIN_ATTEMPT_REASON_FILTER_OPTIONS,
        },
      }),
      columnHelper.accessor((row) => (row.passed ? 'true' : 'false'), {
        id: 'passed',
        header: 'Passed',
        cell: (info) => {
          const passed = info.row.original.passed;
          return (
            <Badge bg={passed ? 'success' : 'danger'}>
              {passed ? 'Yes' : 'No'}
            </Badge>
          );
        },
        meta: {
          filterComponent: 'SelectFilter',
          filterOptions: FACE_LOGIN_PASSED_FILTER_OPTIONS,
        },
      }),
      columnHelper.accessor((row) => `${row.attempt}/${row.maxAttempts}`, {
        id: 'attempt',
        header: 'Attempt',
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.bestScore, {
        id: 'score',
        header: 'Score',
        cell: (info) => {
          const attempt = info.row.original;
          const scoreText =
            attempt.bestScore == null
              ? ''
              : `${formatDecimal(attempt.bestScore, 3)}${
                  attempt.effectiveCosineThreshold != null
                    ? ` / ${formatDecimal(attempt.effectiveCosineThreshold, 3)}`
                    : ''
                }`;
          return <MetricValue value={scoreText} warning={scoreWarning(attempt)} />;
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.bestSharpness, {
        id: 'sharpness',
        header: 'Sharpness',
        cell: (info) => {
          const attempt = info.row.original;
          return (
            <MetricValue
              value={formatDecimal(attempt.bestSharpness, 1)}
              warning={sharpnessWarning(attempt)}
            />
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.bestBrightness, {
        id: 'brightness',
        header: 'Brightness',
        cell: (info) => {
          const attempt = info.row.original;
          return (
            <MetricValue
              value={
                attempt.bestBrightness == null
                  ? ''
                  : formatDecimal(attempt.bestBrightness, 0)
              }
              warning={brightnessWarning(attempt)}
            />
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.maxFaceCount, {
        id: 'faces',
        header: 'Faces',
        cell: (info) => {
          const attempt = info.row.original;
          return (
            <MetricValue
              value={attempt.maxFaceCount == null ? '' : String(attempt.maxFaceCount)}
              warning={facesWarning(attempt)}
            />
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.durationSeconds, {
        id: 'duration',
        header: 'Duration',
        cell: (info) => `${formatDecimal(info.getValue(), 1) ?? ''}s`,
        enableColumnFilter: false,
      }),
    ],
    [columnHelper, expandedRowId]
  );

  if (!loading && attempts.length === 0 && (props.total ?? 0) === 0) {
    return <p className="text-muted mb-0">No face login attempts recorded for this inmate.</p>;
  }

  return (
    <BaseHtmlTable
      rows={attempts}
      columnDefs={columns}
      paginationMode="server"
      page={props.page}
      totalPages={props.totalPages}
      total={props.total}
      perPage={props.perPage}
      loading={loading}
      striped={false}
      serverPagination={props.serverPagination}
      onServerPaginationChange={props.onServerPaginationChange}
      serverSorting={props.serverSorting}
      onServerSortingChange={props.onServerSortingChange}
      serverColumnFilters={props.serverColumnFilters}
      onServerColumnFiltersChange={props.onServerColumnFiltersChange}
      getRowClassName={(row: FaceLoginAttempt) => {
        const sessionIndex = sessionIndexes.get(row.sessionId) ?? 0;
        return sessionIndex % 2 === 1 ? 'table-light' : '';
      }}
      isRowExpanded={(row: Row<FaceLoginAttempt>) => expandedRowId === row.original.id}
      getExpansionRow={(row: Row<FaceLoginAttempt>) => {
        const attempt = row.original;
        const modeLabel =
          FACE_LOGIN_MODE_LABELS[attempt.mode as keyof typeof FACE_LOGIN_MODE_LABELS] ??
          String(attempt.mode);
        return (
          <tr>
            <td
              colSpan={row.getVisibleCells().length}
              className="bg-light"
              style={{ padding: 15 }}>
              <RowLayout>
                <DetailItem label="Session" value={attempt.sessionId} />
                <DetailItem label="Client attempt" value={attempt.clientAttemptId} />
                <DetailItem label="Mode" value={modeLabel} />
                <DetailItem
                  label="Low performance device"
                  value={attempt.isLowPerformanceDevice ? 'Yes' : 'No'}
                />
                <DetailItem
                  label="Yaw"
                  value={formatDecimal(attempt.bestYaw, 1)}
                  warning={poseWarning(attempt)}
                />
                <DetailItem
                  label="Roll"
                  value={formatDecimal(attempt.bestRoll, 1)}
                  warning={poseWarning(attempt)}
                />
                <DetailItem
                  label="Face area"
                  value={formatDecimal(attempt.bestFaceArea, 3)}
                  warning={faceAreaWarning(attempt)}
                />
                <DetailItem
                  label="Consensus"
                  value={
                    attempt.consensusMatchingCount == null && attempt.consensusTotal == null
                      ? ''
                      : `${attempt.consensusMatchingCount ?? ''}/${attempt.consensusTotal ?? ''}`
                  }
                />
                <DetailItem label="Frames processed" value={attempt.framesProcessed} />
                <DetailItem label="Frames with face" value={attempt.framesWithFace} />
                <DetailItem label="Frames no face" value={attempt.framesNoFace} />
                <DetailItem label="Frames too close" value={attempt.framesTooClose} />
                <DetailItem label="Frames too far" value={attempt.framesTooFar} />
                <DetailItem label="Frames low sharpness" value={attempt.framesLowSharpness} />
                <DetailItem label="Frames pose rejected" value={attempt.framesPoseRejected} />
                <DetailItem label="Frames matched" value={attempt.framesMatched} />
                <DetailItem
                  label="Min / avg sharpness"
                  value={`${formatDecimal(attempt.minSharpness, 1) ?? ''} / ${formatDecimal(attempt.averageSharpness, 1) ?? ''}`}
                />
                <DetailItem
                  label="Min / avg brightness"
                  value={`${formatDecimal(attempt.minBrightness, 0) ?? ''} / ${formatDecimal(attempt.averageBrightness, 0) ?? ''}`}
                />
                <DetailItem
                  label="Device"
                  value={
                    attempt.mobileDevice?.id == null ? (
                      ''
                    ) : (
                      <span>
                        <Link to={`/mobile-device/view/${attempt.mobileDevice.id}`}>
                          <i className="fas fa-mobile" /> {attempt.mobileDevice.name}
                        </Link>
                        {attempt.mobileDevice.osVersion != null ? (
                          <small> (iOS {attempt.mobileDevice.osVersion})</small>
                        ) : null}
                      </span>
                    )
                  }
                />
                <DetailItem label="App version" value={attempt.appVersion ?? ''} />
              </RowLayout>
            </td>
          </tr>
        );
      }}
    />
  );
};

export default FaceLoginAttemptsTable;
